import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTeacherProfile } from '../../api/teacherProfile';
import { fetchDomains } from '../../api/standards';
import { createClass, generateScope, reorderUnits } from '../../api/classes';
import UnitReorderList from './UnitReorderList';
import LessonGenerationPanel from './LessonGenerationPanel';
import styles from './ClassOnboardPage.module.css';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const SUBJECTS = ['Math', 'ELA', 'Science', 'Social Studies', 'Other'];

const SUBJECT_MAP = {
  'Math': 'Mathematics',
  'ELA': 'English Language Arts',
};

function generateClassNameFromFields(grade, subject) {
  if (!grade || !subject) return '';
  const gradeLabel = grade === 'K' ? 'Kindergarten' : `${grade}th Grade`;
  return `${gradeLabel} ${subject}`;
}

function formatDateForInput(dateValue) {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

export default function ClassOnboardPage() {
  const navigate = useNavigate();

  // Phase: 'form' | 'units' | 'lessons'
  const [phase, setPhase] = useState('form');

  // Profile state
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Form fields
  const [gradeLevel, setGradeLevel] = useState('');
  const [subject, setSubject] = useState('');
  const [className, setClassName] = useState('');
  const [classNameManual, setClassNameManual] = useState(false);
  const [section, setSection] = useState('');
  const [yearStart, setYearStart] = useState('');
  const [yearEnd, setYearEnd] = useState('');
  const [lessonDuration, setLessonDuration] = useState(60);
  const [scheduleType, setScheduleType] = useState('daily');
  const [useDifferentDates, setUseDifferentDates] = useState(false);

  // Domains
  const [availableDomains, setAvailableDomains] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);

  // Requirements textarea
  const [requirements, setRequirements] = useState('');

  // Three-phase state
  const [classId, setClassId] = useState(null);
  const [units, setUnits] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Track original grade/subject/domains for change detection on back-nav
  const [savedGrade, setSavedGrade] = useState('');
  const [savedSubject, setSavedSubject] = useState('');

  // Fetch teacher profile on mount
  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      try {
        const res = await fetchTeacherProfile();
        if (cancelled) return;
        const p = res.profile || res.data || res;
        setProfile(p);

        // Pre-fill from profile
        if (p.gradesJson && p.gradesJson.length > 0) {
          setGradeLevel(p.gradesJson[0]);
        }
        if (p.subjectsJson && p.subjectsJson.length > 0) {
          setSubject(p.subjectsJson[0]);
        }
        if (p.yearStart) {
          setYearStart(formatDateForInput(p.yearStart));
        }
        if (p.yearEnd) {
          setYearEnd(formatDateForInput(p.yearEnd));
        }
        if (p.lessonDuration) {
          setLessonDuration(p.lessonDuration);
        }
        if (p.scheduleType) {
          setScheduleType(p.scheduleType);
        }
      } catch (err) {
        console.error('Failed to load teacher profile:', err);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    }
    loadProfile();
    return () => { cancelled = true; };
  }, []);

  // Auto-generate class name when grade or subject changes
  useEffect(() => {
    if (!classNameManual) {
      setClassName(generateClassNameFromFields(gradeLevel, subject));
    }
  }, [gradeLevel, subject, classNameManual]);

  // Fetch domains when grade + subject change
  useEffect(() => {
    if (!gradeLevel || !subject) {
      setAvailableDomains([]);
      setSelectedDomains([]);
      return;
    }

    let cancelled = false;
    async function loadDomains() {
      setDomainsLoading(true);
      try {
        const mappedSubject = SUBJECT_MAP[subject] || subject;
        const res = await fetchDomains(gradeLevel, mappedSubject);
        if (cancelled) return;
        const domains = res.data || res;
        setAvailableDomains(domains);
        // Select all domains by default
        setSelectedDomains(domains.map(d => d.domainCode));
      } catch (err) {
        console.error('Failed to load domains:', err);
        if (!cancelled) setAvailableDomains([]);
      } finally {
        if (!cancelled) setDomainsLoading(false);
      }
    }
    loadDomains();
    return () => { cancelled = true; };
  }, [gradeLevel, subject]);

  const profileGrades = profile?.gradesJson || [];
  const profileSubjects = profile?.subjectsJson || [];

  const toggleDomain = (domainCode) => {
    setSelectedDomains(prev =>
      prev.includes(domainCode)
        ? prev.filter(c => c !== domainCode)
        : [...prev, domainCode]
    );
  };

  const handleClassNameChange = (e) => {
    setClassName(e.target.value);
    setClassNameManual(true);
  };

  // Phase 1 -> Phase 2: Generate Units
  const handleGenerateUnits = async () => {
    setError('');

    if (!gradeLevel) { setError('Please select a grade level.'); return; }
    if (!subject) { setError('Please select a subject.'); return; }
    if (!className.trim()) { setError('Please enter a class name.'); return; }
    if (!yearStart) { setError('Please set the school year start date.'); return; }
    if (!yearEnd) { setError('Please set the school year end date.'); return; }

    setGenerating(true);
    try {
      // Step 1: Create the class
      const classData = {
        name: className.trim(),
        section: section.trim() || undefined,
        subject,
        gradeLevel,
        state: profile?.state || undefined,
        yearStart,
        yearEnd,
        mathMinutes: lessonDuration,
        scheduleType,
        classDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      };
      const created = await createClass(classData);
      const newClassId = created.id || created.data?.id;
      setClassId(newClassId);

      // Step 2: Generate scope & sequence
      const scopeResult = await generateScope(newClassId, {
        preferences: requirements.trim() || undefined,
        selectedDomains: selectedDomains.length > 0 ? selectedDomains : undefined,
      });
      setUnits(scopeResult.units);

      // Save current values for change detection
      setSavedGrade(gradeLevel);
      setSavedSubject(subject);
      setPhase('units');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to generate units. Please try again.';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  };

  // Phase 2 -> Phase 3: Save unit order & continue
  const handleSaveOrder = async () => {
    setError('');
    try {
      const unitOrder = units.map(u => u.id);
      const result = await reorderUnits(classId, unitOrder);
      setUnits(result.units);
      setPhase('lessons');
    } catch (err) {
      setError('Failed to save unit order.');
    }
  };

  // Back: Phase 2 -> Phase 1
  const handleEditDetails = () => {
    setPhase('form');
  };

  // Back: Phase 3 -> Phase 2
  const handleReorderUnits = () => {
    setPhase('units');
  };

  // Back: Phase 3 -> Phase 1
  const handleEditDetailsFromLessons = () => {
    const gradeChanged = gradeLevel !== savedGrade;
    const subjectChanged = subject !== savedSubject;
    if (gradeChanged || subjectChanged) {
      if (!window.confirm('Changing grade or subject will regenerate your units. Continue?')) {
        return;
      }
    }
    setPhase('form');
  };

  const getPhaseTitle = () => {
    switch (phase) {
      case 'form': return 'Create a New Class';
      case 'units': return 'Review & Reorder Units';
      case 'lessons': return 'Generate Lessons';
      default: return 'Create a New Class';
    }
  };

  const getPhaseSubtitle = () => {
    switch (phase) {
      case 'form': return 'Set up your class details and we\'ll generate a scope & sequence.';
      case 'units': return 'Drag to reorder your units, then continue to lesson generation.';
      case 'lessons': return 'Generate lessons for each unit in your scope & sequence.';
      default: return '';
    }
  };

  if (profileLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <p className={styles.loadingText}>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{getPhaseTitle()}</h1>
          <p className={styles.subtitle}>{getPhaseSubtitle()}</p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Phase 1: Form */}
        {phase === 'form' && (
          <div className={styles.form}>
            {/* Grade Level */}
            <div className={styles.field}>
              <label className={styles.label}>Grade Level</label>
              <div className={styles.buttonGroup}>
                {GRADES.map(g => (
                  <button
                    key={g}
                    type="button"
                    className={`${styles.toggleBtn} ${gradeLevel === g ? styles.toggleBtnActive : ''} ${profileGrades.includes(g) && gradeLevel !== g ? styles.toggleBtnHighlighted : ''}`}
                    onClick={() => setGradeLevel(g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {profileGrades.length > 0 && (
                <p className={styles.prefilledNote}>
                  Highlighted grades are from your profile
                </p>
              )}
            </div>

            {/* Subject */}
            <div className={styles.field}>
              <label className={styles.label}>Subject</label>
              <div className={styles.buttonGroup}>
                {SUBJECTS.map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`${styles.toggleBtn} ${subject === s ? styles.toggleBtnActive : ''} ${profileSubjects.includes(s) && subject !== s ? styles.toggleBtnHighlighted : ''}`}
                    onClick={() => setSubject(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {profileSubjects.length > 0 && (
                <p className={styles.prefilledNote}>
                  Highlighted subjects are from your profile
                </p>
              )}
            </div>

            {/* Class Name */}
            <div className={styles.field}>
              <label className={styles.label}>Class Name</label>
              <input
                type="text"
                className={styles.input}
                value={className}
                onChange={handleClassNameChange}
                placeholder="e.g., 5th Grade Math"
              />
              {!classNameManual && className && (
                <p className={styles.prefilledNote}>Auto-generated from grade and subject</p>
              )}
            </div>

            {/* Section */}
            <div className={styles.field}>
              <label className={styles.label}>Section (optional)</label>
              <input
                type="text"
                className={styles.input}
                value={section}
                onChange={(e) => setSection(e.target.value)}
                placeholder="e.g., Period 3, Block A"
              />
            </div>

            {/* School Year Dates */}
            <div className={styles.field}>
              <label className={styles.label}>School Year Dates</label>
              {profile?.yearStart && profile?.yearEnd && !useDifferentDates && (
                <p className={styles.prefilledNote}>
                  Pre-filled from your profile: {formatDateForInput(profile.yearStart)} to {formatDateForInput(profile.yearEnd)}
                </p>
              )}
              <label className={styles.dateToggle}>
                <input
                  type="checkbox"
                  checked={useDifferentDates}
                  onChange={(e) => setUseDifferentDates(e.target.checked)}
                />
                Use different dates for this class
              </label>
              {(useDifferentDates || !profile?.yearStart) && (
                <div className={styles.dateRow}>
                  <div>
                    <label className={styles.label}>Start Date</label>
                    <input
                      type="date"
                      className={styles.input}
                      value={yearStart}
                      onChange={(e) => setYearStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={styles.label}>End Date</label>
                    <input
                      type="date"
                      className={styles.input}
                      value={yearEnd}
                      onChange={(e) => setYearEnd(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Lesson Duration */}
            <div className={styles.field}>
              <label className={styles.label}>Lesson Duration (minutes)</label>
              <input
                type="number"
                className={styles.input}
                value={lessonDuration}
                onChange={(e) => setLessonDuration(Number(e.target.value))}
                min={10}
                max={180}
              />
              {profile?.lessonDuration && (
                <p className={styles.prefilledNote}>Pre-filled from your profile</p>
              )}
            </div>

            {/* Schedule Type */}
            <div className={styles.field}>
              <label className={styles.label}>Schedule Type</label>
              <div className={styles.buttonGroup}>
                {[
                  { value: 'daily', label: 'Daily' },
                  { value: 'alternating', label: 'Alternating' },
                  { value: 'block', label: 'Block' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.toggleBtn} ${scheduleType === opt.value ? styles.toggleBtnActive : ''}`}
                    onClick={() => setScheduleType(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {profile?.scheduleType && (
                <p className={styles.prefilledNote}>Pre-filled from your profile</p>
              )}
            </div>

            {/* Standards Domains */}
            {gradeLevel && subject && (
              <div className={styles.field}>
                <label className={styles.label}>Standards Domains to Cover</label>
                {domainsLoading ? (
                  <p className={styles.loadingText}>Loading domains...</p>
                ) : availableDomains.length > 0 ? (
                  <div className={styles.domainGrid}>
                    {availableDomains.map(d => (
                      <div
                        key={d.domainCode}
                        className={`${styles.domainCard} ${selectedDomains.includes(d.domainCode) ? styles.domainCardSelected : ''}`}
                        onClick={() => toggleDomain(d.domainCode)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDomain(d.domainCode); } }}
                      >
                        <div className={styles.domainCode}>{d.domainCode}</div>
                        <div className={styles.domainName}>{d.domainName}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={styles.prefilledNote}>No standards domains found for this grade and subject.</p>
                )}
              </div>
            )}

            {/* Requirements textarea */}
            <div className={styles.field}>
              <label className={styles.label}>Any requirements to keep in mind? (optional)</label>
              <textarea
                className={styles.textarea}
                value={requirements}
                onChange={(e) => setRequirements(e.target.value)}
                placeholder="E.g., focus on test prep for state assessment, emphasize fractions, skip geometry..."
                rows={3}
              />
            </div>

            <button
              className={styles.saveBtn}
              onClick={handleGenerateUnits}
              disabled={generating || !gradeLevel || !subject || !className.trim() || !yearStart || !yearEnd}
            >
              {generating ? 'Creating class and generating units...' : 'Generate Units'}
            </button>
          </div>
        )}

        {/* Phase 2: Units */}
        {phase === 'units' && (
          <>
            <div className={styles.formSummary}>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Class</span>
                  <span className={styles.summaryValue}>{className}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Grade</span>
                  <span className={styles.summaryValue}>{gradeLevel === 'K' ? 'Kindergarten' : `Grade ${gradeLevel}`}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Subject</span>
                  <span className={styles.summaryValue}>{subject}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Year</span>
                  <span className={styles.summaryValue}>{yearStart} to {yearEnd}</span>
                </div>
              </div>
              <div className={styles.summaryActions}>
                <button
                  type="button"
                  className={styles.editLink}
                  onClick={handleEditDetails}
                >
                  Edit Details
                </button>
              </div>
            </div>

            <UnitReorderList
              units={units}
              onReorder={setUnits}
            />

            <div className={styles.phaseActions}>
              <button
                className={styles.continueBtn}
                onClick={handleSaveOrder}
              >
                Save &amp; Continue
              </button>
            </div>
          </>
        )}

        {/* Phase 3: Lessons */}
        {phase === 'lessons' && (
          <>
            <div className={styles.formSummary}>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Class</span>
                  <span className={styles.summaryValue}>{className}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Grade</span>
                  <span className={styles.summaryValue}>{gradeLevel === 'K' ? 'Kindergarten' : `Grade ${gradeLevel}`}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Subject</span>
                  <span className={styles.summaryValue}>{subject}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Year</span>
                  <span className={styles.summaryValue}>{yearStart} to {yearEnd}</span>
                </div>
              </div>
              <div className={styles.summaryActions}>
                <button
                  type="button"
                  className={styles.editLink}
                  onClick={handleEditDetailsFromLessons}
                >
                  Edit Details
                </button>
                <button
                  type="button"
                  className={styles.editLink}
                  onClick={handleReorderUnits}
                >
                  Reorder Units
                </button>
              </div>
            </div>

            <LessonGenerationPanel
              classId={classId}
              units={units}
            />

            <div className={styles.phaseActions}>
              <button
                className={styles.dashboardBtn}
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
